import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';


import MetricsCard from './components/MetricsCard';
import LocationFilter from './components/LocationFilter';
import PropertyGrid from './components/PropertyGrid';
import PropertyDetails from './components/PropertyDetails';
import FilterBar from './components/FilterBar';

import BulkActionsToolbar from './components/BulkActionsToolbar';
import SyncStatusIndicator from './components/SyncStatusIndicator';
import DeleteApprovalModal from './components/DeleteApprovalModal';
import NotificationToast from './components/NotificationToast';
import { exportPropertiesToExcel, exportFilteredPropertiesToExcel } from '../../utils/excelExport';


import FieldManagementModal from './components/FieldManagementModal';
import PropertyFormModal from './components/PropertyFormModal';
import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import PropertiesPopupModal from './components/PropertiesPopupModal';





const PropertyManagementDashboard = () => {
  const { user } = useAuth();
  const [userRole] = useState('admin');
  const [userName] = useState('أحمد محمد');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [syncStatus, setSyncStatus] = useState('connected');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    savedFilter: '',
    propertyType: 'all',
    status: 'all',
    search: '',
    searchBy: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [showFieldManagement, setShowFieldManagement] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [propertyFormMode, setPropertyFormMode] = useState('add');
  const [editingProperty, setEditingProperty] = useState(null);
  const [quickMenuOpen, setQuickMenuOpen] = useState(false);
  const [showPropertiesPopup, setShowPropertiesPopup] = useState(false);
  const [showNewContractModal, setShowNewContractModal] = useState(false);

  const [properties, setProperties] = useState([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [propertiesError, setPropertiesError] = useState(null);

  const breadcrumbItems = [
  { label: 'الرئيسية', path: '/', icon: 'Home' },
  { label: 'لوحة التحكم', path: '/property-management-dashboard', icon: 'Building2' }];

  const locationData = [
  {
    id: 'abdoun',
    area: 'عبدون',
    count: 456,
    districts: [
    { id: 'abdoun_north', name: 'عبدون الشمالي', count: 123 },
    { id: 'abdoun_south', name: 'عبدون الجنوبي', count: 89 },
    { id: 'sweifieh', name: 'السويفية', count: 67 },
    { id: 'umm_uthaina', name: 'أم أذينة', count: 98 },
    { id: 'shmeisani', name: 'الشميساني', count: 79 }]

  },
  {
    id: 'khalda',
    area: 'خلدا',
    count: 389,
    districts: [
    { id: 'khalda_center', name: 'خلدا الوسط', count: 102 },
    { id: 'gardens', name: 'الجاردنز', count: 87 },
    { id: 'tla_ali', name: 'تلاع العلي', count: 76 },
    { id: 'deir_ghbar', name: 'دير غبار', count: 124 }]

  },
  {
    id: 'marka',
    area: 'ماركا',
    count: 234,
    districts: [
    { id: 'new_marka', name: 'ماركا الجديدة', count: 78 },
    { id: 'hashmi_shamali', name: 'الهاشمي الشمالي', count: 92 },
    { id: 'quwaismeh', name: 'القويسمة', count: 64 }]

  },
  {
    id: 'jabal_amman',
    area: 'جبل عمان',
    count: 168,
    districts: [
    { id: 'first_circle', name: 'الدوار الأول', count: 89 },
    { id: 'rainbow_street', name: 'شارع الرينبو', count: 79 }]

  }];


  // NEW: Load properties from Supabase
  useEffect(() => {
    if (user?.id) {
      loadProperties();
    }
  }, [user?.id]);

  const loadProperties = async () => {
    try {
      setIsLoadingProperties(true);
      setPropertiesError(null);
      
      const data = await propertyService?.getAll(user?.id);
      setProperties(data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
      setPropertiesError(error?.message || 'فشل في تحميل العقارات');
    } finally {
      setIsLoadingProperties(false);
    }
  };

  // Filter properties based on filters
  const filteredProperties = React.useMemo(() => {
    return properties?.filter((property) => {
      // Property type filter
      if (filters?.propertyType !== 'all' && property?.property_type !== filters?.propertyType) {
        return false;
      }

      // Status filter
      if (filters?.status !== 'all' && property?.status !== filters?.status) {
        return false;
      }

      // Search filter
      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        const searchFields = [
          property?.property_id,
          property?.title,
          property?.address,
          property?.city,
          property?.district
        ];
        
        const matches = searchFields?.some(field => 
          field?.toLowerCase()?.includes(searchLower)
        );
        
        if (!matches) return false;
      }

      // Location filter
      if (selectedLocations?.length > 0) {
        const propertyLocation = property?.city || property?.district;
        const matchesLocation = selectedLocations?.some(loc => 
          propertyLocation?.includes(loc)
        );
        if (!matchesLocation) return false;
      }

      return true;
    });
  }, [properties, filters, selectedLocations]);

  // Calculate metrics from real data
  const metrics = React.useMemo(() => {
    const total = properties?.length || 0;
    const available = properties?.filter(p => p?.status === 'متاح')?.length || 0;
    const rented = properties?.filter(p => p?.status === 'مؤجر')?.length || 0;
    const sold = properties?.filter(p => p?.status === 'مباع')?.length || 0;

    return {
      total,
      available,
      rented,
      sold
    };
  }, [properties]);

  const calculateMetrics = () => {
    const totalProperties = filteredProperties?.length || 0;
    const availableProperties = filteredProperties?.filter((p) => p?.status === 'متاح')?.length || 0;
    const rentedProperties = filteredProperties?.filter((p) => p?.status === 'مؤجر')?.length || 0;
    const totalValue = filteredProperties?.reduce((sum, p) => sum + (p?.price || 0), 0) || 0;

    return [
    {
      title: 'إجمالي العقارات',
      value: totalProperties,
      icon: 'Building2',
      trend: { value: 12, isPositive: true },
      type: 'total',
      onClick: () => handleMetricClick('total')
    },
    {
      title: 'العقارات المتاحة',
      value: availableProperties,
      icon: 'Home',
      trend: { value: 8, isPositive: true },
      type: 'available',
      onClick: () => handleMetricClick('available')
    },
    {
      title: 'العقارات المؤجرة',
      value: rentedProperties,
      icon: 'Key',
      trend: { value: 5, isPositive: false },
      type: 'rented',
      onClick: () => handleMetricClick('rented')
    },
    {
      title: 'القيمة الإجمالية',
      value: totalValue,
      icon: 'DollarSign',
      currency: 'د.أ',
      trend: { value: 15, isPositive: true },
      type: 'value',
      onClick: () => handleMetricClick('value')
    }];

  };

  useEffect(() => {
    const syncInterval = setInterval(() => {
      setSyncStatus('syncing');
      setTimeout(() => {
        setSyncStatus('connected');
      }, 2000);
    }, 30000);

    return () => clearInterval(syncInterval);
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = (searchTerm, searchBy) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      searchBy: searchBy
    }));
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations((prev) =>
    prev?.includes(locationId) ?
    prev?.filter((id) => id !== locationId) :
    [...prev, locationId]
    );
  };

  // Add this block - Define missing handler functions
  const handleQuickAction = (action) => {
    if (typeof action?.onClick === 'function') {
      action?.onClick();
    }
  };

  const handleLocationChange = (locationId) => {
    handleLocationToggle(locationId);
  };

  const handlePropertySelect = (propertyId) => {
    setSelectedProperties((prev) =>
    prev?.includes(propertyId) ?
    prev?.filter((id) => id !== propertyId) :
    [...prev, propertyId]
    );
  };

  const handleDeleteProperty = (propertyId) => {
    handlePropertyDelete(propertyId);
  };

  const handleCloseDetails = () => {
    setSelectedProperty(null);
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setPropertyFormMode('edit');
    setShowPropertyForm(true);
    setSelectedProperty(null);
  };

  const confirmDelete = (deletionDetails) => {
    handleDeleteConfirm(deletionDetails);
  };

  const handleFieldsUpdate = (fields) => {
    handleSaveCustomFields(fields);
  };

  const handleQuickMenuToggle = () => {
    setQuickMenuOpen((prev) => !prev);
  };

  const savedFilters = [];

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
  };

  const handleBulkAction = (action) => {
    if (action === 'clear') {
      setSelectedProperties([]);
      return;
    }

    if (selectedProperties?.length === 0) {
      setNotification({
        type: 'warning',
        title: 'تنبيه',
        message: 'يرجى اختيار عقار واحد على الأقل',
        timestamp: new Date()?.toISOString()
      });
      return;
    }

    switch (action) {
      case 'update_status':
        setNotification({
          type: 'info',
          title: 'تحديث الحالة',
          message: `جاري تحديث حالة ${selectedProperties?.length} عقار`,
          timestamp: new Date()?.toISOString()
        });
        break;
      case 'notify_owners':
        setNotification({
          type: 'success',
          title: 'إشعار الملاك',
          message: `تم إرسال إشعارات لملاك ${selectedProperties?.length} عقار`,
          timestamp: new Date()?.toISOString()
        });
        break;
      case 'generate_contracts':
        setNotification({
          type: 'success',
          title: 'إنشاء عقود',
          message: `جاري إنشاء عقود لـ ${selectedProperties?.length} عقار`,
          timestamp: new Date()?.toISOString()
        });
        break;
      case 'export_selected':
        const selectedProps = properties?.filter((p) => selectedProperties?.includes(p?.id));
        exportPropertiesToExcel(selectedProps, 'selected_properties');
        setNotification({
          type: 'success',
          title: 'تصدير ناجح',
          message: `تم تصدير ${selectedProperties?.length} عقار إلى Excel`,
          timestamp: new Date()?.toISOString()
        });
        break;
      case 'delete':
        handleBulkDelete();
        break;
      default:
        console.log(`Bulk action: ${action} on ${selectedProperties?.length} properties`);
    }
  };

  const handleSaveFilter = () => {
    const filterName = prompt('أدخل اسم الفلتر:');
    if (filterName) {
      setNotification({
        type: 'success',
        title: 'تم الحفظ',
        message: `تم حفظ الفلتر "${filterName}" بنجاح`,
        timestamp: new Date()?.toISOString()
      });
      console.log('Saving filter:', filterName, filters);
    }
  };

  const handleExport = () => {
    try {
      if (filteredProperties?.length === 0) {
        setNotification({
          type: 'warning',
          title: 'تنبيه',
          message: 'لا توجد عقارات لتصديرها',
          timestamp: new Date()?.toISOString()
        });
        return;
      }
      exportPropertiesToExcel(filteredProperties, 'filtered_properties');
      setNotification({
        type: 'success',
        title: 'تصدير ناجح',
        message: `تم تصدير ${filteredProperties?.length} عقار إلى Excel`,
        timestamp: new Date()?.toISOString()
      });
    } catch (error) {
      console.error('Export error:', error);
      setNotification({
        type: 'error',
        title: 'خطأ',
        message: 'فشل تصدير البيانات',
        timestamp: new Date()?.toISOString()
      });
    }
  };

  // Add this block - Define activeFilters based on current filters
  const activeFilters = {
    status: filters?.status || 'all',
    type: filters?.propertyType || 'all',
    search: filters?.search || ''
  };

  const handleDeleteProperties = async (deletionDetails) => {
    try {
      const deletedCount = deletionDetails?.propertyIds?.length;

      // Delete from Supabase
      for (const propertyId of deletionDetails?.propertyIds) {
        await propertyService?.delete(propertyId, user?.id);
      }

      // Filter out deleted properties from local state
      setProperties((prevProperties) =>
        prevProperties?.filter(
          (prop) => !deletionDetails?.propertyIds?.includes(prop?.id)
        )
      );

      // Show success notification
      setNotification({
        type: 'success',
        title: 'تم الحذف بنجاح',
        message: `تم حذف ${deletedCount} عقار بنجاح. السبب: ${deletionDetails?.reason}`,
        timestamp: new Date()?.toISOString()
      });

      // Clear selection
      setSelectedProperties([]);
    } catch (error) {
      console.error('Error deleting properties:', error);
      setNotification({
        type: 'error',
        title: 'خطأ',
        message: error?.message || 'فشل في حذف العقارات',
        timestamp: new Date()?.toISOString()
      });
    }
  };

  const handleBulkDelete = () => {
    if (selectedProperties?.length === 0) {
      setNotification({
        type: 'warning',
        title: 'تنبيه',
        message: 'يرجى اختيار عقار واحد على الأقل للحذف',
        timestamp: new Date()?.toISOString()
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  const handlePropertyDelete = (propertyId) => {
    setSelectedProperties([propertyId]);
    setDeleteModalOpen(true);
  };

  // Add this block - Define missing handler functions
  const handleExportExcel = () => {
    handleExportToExcel();
  };

  const handleDeleteConfirm = (deletionDetails) => {
    handleDeleteProperties(deletionDetails);
    setDeleteModalOpen(false);
  };

  const handleSaveCustomFields = (fields) => {
    setCustomFields(fields);
    setShowFieldManagement(false);
    showNotification('تم حفظ الحقول المخصصة بنجاح', 'success');
  };
  // End of added block

  const quickActions = [
  {
    label: 'إدارة الحقول',
    icon: 'Settings',
    onClick: () => setShowFieldManagement(true),
    roles: ['admin', 'property_manager']
  },
  {
    label: 'تصدير Excel',
    icon: 'Download',
    onClick: handleExportExcel,
    roles: ['admin', 'property_manager']
  },
  {
    label: 'إضافة عقار',
    icon: 'Plus',
    onClick: () => handleAddProperty(),
    roles: ['admin', 'property_manager']
  }];



  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Add this block - Move mockProperties definition before handleExportToExcel
  const mockProperties = [
  {
    id: 'prop001',
    propertyId: 'AMM-2026-001',
    image: "https://images.unsplash.com/photo-1680433328065-5a78a82fa5b2",
    imageAlt: 'Modern luxury apartment building with glass facade and contemporary architecture in Amman city center',
    address: 'شارع الملك عبدالله الثاني، برج الأعمال',
    district: 'عبدون، عمان',
    type: 'شقة',
    realEstateType: 'سكني',
    status: 'متاح',
    owner: 'خالد بن عبدالله',
    ownerPhone: '+962791234567',
    price: 450000,
    currency: 'د.أ',
    profit: 67500,
    lastUpdate: '2026-01-08T14:30:00',
    lat: 31.9539,
    lng: 35.9106,
    remarks: 'عقار في موقع استراتيجي بالقرب من المراكز التجارية',
    videos: ['https://www.w3schools.com/html/mov_bbb.mp4']
  },
  {
    id: 'prop002',
    propertyId: 'AMM-2026-045',
    image: "https://images.unsplash.com/photo-1637663252107-532f77df298f",
    imageAlt: 'Elegant white villa with Mediterranean style architecture featuring arched windows and palm trees in residential setting',
    address: 'حي الجاردنز، فيلا رقم 234',
    district: 'خلدا، عمان',
    type: 'فيلا',
    realEstateType: 'سكني',
    status: 'مؤجر',
    owner: 'سارة أحمد',
    ownerPhone: '+962792345678',
    price: 1200000,
    currency: 'د.أ',
    profit: 180000,
    lastUpdate: '2026-01-07T10:15:00',
    lat: 31.9454,
    lng: 35.8381,
    remarks: 'فيلا فاخرة مع حديقة واسعة ومسبح خاص',
    videos: ['https://www.w3schools.com/html/movie.mp4']
  },
  {
    id: 'prop003',
    propertyId: 'AMM-2026-089',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b9700dd-1766496743201.png",
    imageAlt: 'Professional office space with modern glass partitions, ergonomic furniture and city skyline view through large windows',
    address: 'شارع الشميساني، مركز الأعمال',
    district: 'الشميساني، عمان',
    type: 'مكتب',
    realEstateType: 'تجاري',
    status: 'محجوز',
    owner: 'محمد العتيبي',
    ownerPhone: '+962793456789',
    price: 320000,
    currency: 'د.أ',
    profit: 48000,
    lastUpdate: '2026-01-09T16:45:00',
    lat: 31.9606,
    lng: 35.9066,
    remarks: 'مكتب تجاري بمساحة 200 متر مربع مع موقف سيارات'
  },
  {
    id: 'prop004',
    propertyId: 'AMM-2026-023',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1153eaf97-1764681179487.png",
    imageAlt: 'Commercial retail storefront with large display windows and modern signage on busy shopping street',
    address: 'شارع مكة المكرمة، محل تجاري',
    district: 'ماركا الجديدة، عمان',
    type: 'محل',
    realEstateType: 'تجاري',
    status: 'متاح',
    owner: 'فاطمة الدوسري',
    ownerPhone: '+962794567890',
    price: 280000,
    currency: 'د.أ',
    profit: 42000,
    lastUpdate: '2026-01-06T09:20:00',
    lat: 32.0128,
    lng: 35.9764,
    remarks: 'محل تجاري على شارع رئيسي بواجهة زجاجية'
  },
  {
    id: 'prop005',
    propertyId: 'AMM-2026-012',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_174c488a2-1764688098798.png",
    imageAlt: 'Vacant residential land plot with clear boundaries and mountain backdrop in developing urban area',
    address: 'حي الدوار الأول، أرض سكنية',
    district: 'جبل عمان، عمان',
    type: 'أرض',
    realEstateType: 'سكني',
    status: 'معلق',
    owner: 'عبدالرحمن القحطاني',
    ownerPhone: '+962795678901',
    price: 850000,
    currency: 'د.أ',
    profit: 127500,
    lastUpdate: '2026-01-05T13:00:00',
    lat: 31.9539,
    lng: 35.9306,
    remarks: 'أرض سكنية بمساحة 600 متر مربع في حي راقي'
  },
  {
    id: 'prop006',
    propertyId: 'AMM-2026-156',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13400e1e9-1768218665686.png",
    imageAlt: 'Spacious three-bedroom apartment interior with modern kitchen, hardwood floors and natural lighting',
    address: 'شارع المدينة المنورة، شقة 405',
    district: 'خلدا، عمان',
    type: 'شقة',
    realEstateType: 'سكني',
    status: 'صيانة',
    owner: 'نورة السديري',
    ownerPhone: '+962796789012',
    price: 380000,
    currency: 'د.أ',
    profit: 57000,
    lastUpdate: '2026-01-10T11:30:00',
    lat: 31.9454,
    lng: 35.8381,
    remarks: 'شقة حديثة تحت الصيانة، جاهزة خلال أسبوعين'
  },
  {
    id: 'prop007',
    propertyId: 'AMM-2026-078',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_170d2c073-1767547738384.png",
    imageAlt: 'Luxurious two-story villa with swimming pool, landscaped garden and outdoor entertainment area',
    address: 'شارع تلاع العلي، فيلا فاخرة',
    district: 'تلاع العلي، عمان',
    type: 'فيلا',
    realEstateType: 'سكني',
    status: 'متاح',
    owner: 'طلال الغامدي',
    ownerPhone: '+962797890123',
    keyHolder: 'خالد الغامدي',
    price: 1800000,
    currency: 'د.أ',
    profit: 270000,
    lastUpdate: '2026-01-09T08:45:00',
    lat: 31.9723,
    lng: 35.8579,
    remarks: 'فيلا فاخرة في منطقة راقية مع تشطيبات فاخرة'
  },
  {
    id: 'prop008',
    propertyId: 'AMM-2026-203',
    image: "https://images.unsplash.com/photo-1668694337396-d98d9499eed0",
    imageAlt: 'Corporate office building lobby with marble floors, modern reception desk and professional lighting',
    address: 'برج عبدون، مكتب إداري',
    district: 'عبدون الشمالي، عمان',
    type: 'مكتب',
    realEstateType: 'تجاري',
    status: 'مؤجر',
    owner: 'عبدالله الشمري',
    ownerPhone: '+962798901234',
    keyHolder: 'ناصر الشمري',
    price: 520000,
    currency: 'ر.س',
    profit: 78000,
    lastUpdate: '2026-01-08T15:20:00',
    lat: 31.9566,
    lng: 35.9239
  }];

  const handleExportToExcel = () => {
    try {
      if (activeFilters?.status !== 'all' || activeFilters?.type !== 'all' || activeFilters?.search) {
        exportFilteredPropertiesToExcel(mockProperties, activeFilters, 'filtered_properties');
        showNotification('تم تصدير العقارات المفلترة إلى Excel بنجاح', 'success');
      } else {
        exportPropertiesToExcel(mockProperties, 'all_properties');
        showNotification('تم تصدير جميع العقارات إلى Excel بنجاح', 'success');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('فشل تصدير البيانات', 'error');
    }
  };

  const handleBulkStatusChange = (status) => {
    console.log('Changing status to:', status, 'for properties:', selectedProperties);
    alert(`تم تغيير حالة ${selectedProperties?.length} عقار إلى ${status}`);
    setSelectedProperties([]);
  };

  const handleConfirmDelete = () => {
    console.log('Deleting properties:', selectedProperties);
    alert(`تم حذف ${selectedProperties?.length} عقار`);
    setSelectedProperties([]);
    setDeleteModalOpen(false);
  };

  const handleBulkExport = () => {
    console.log('Exporting properties:', selectedProperties);
    exportFilteredPropertiesToExcel(mockProperties?.filter((p) => selectedProperties?.includes(p?.id)));
  };

  const handleSyncProperties = () => {
    console.log('Syncing properties');
    setSyncStatus('syncing');
    setTimeout(() => {
      setSyncStatus('connected');
      setNotification({ type: 'success', message: 'تم مزامنة العقارات بنجاح' });
    }, 2000);
  };

  const handleAddProperty = () => {
    setEditingProperty(null);
    setPropertyFormMode('add');
    setShowPropertyForm(true);
  };

  // Define actionBoxesConfig after handler functions
  const actionBoxesConfig = [
  {
    id: 'add-property',
    icon: 'Plus',
    label: 'إضافة عقار جديد',
    onClick: () => handleAddProperty(),
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
    borderColor: 'rgba(245, 158, 11, 0.5)'
  },
  {
    id: 'view-all-properties',
    icon: 'Building2',
    label: 'عرض جميع العقارات',
    onClick: () => setShowPropertiesPopup(true),
    color: '#3B82F6',
    bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
    borderColor: 'rgba(59, 130, 246, 0.5)'
  },
  {
    id: 'export-data',
    icon: 'Download',
    label: 'تصدير البيانات',
    onClick: handleExportExcel,
    color: '#10B981',
    bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
    borderColor: 'rgba(16, 185, 129, 0.5)'
  },
  {
    id: 'sync-properties',
    icon: 'RefreshCw',
    label: 'مزامنة العقارات',
    onClick: handleSyncProperties,
    color: '#8B5CF6',
    bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
    borderColor: 'rgba(139, 92, 246, 0.5)'
  }];


  const handleSaveProperty = async (propertyData) => {
    try {
      if (!user) {
        showNotification('يجب تسجيل الدخول أولاً', 'error');
        return;
      }

      if (propertyFormMode === 'add') {
        // Save new property to Supabase
        const savedProperty = await propertyService?.create(propertyData, user?.id);

        // Add to local state
        setProperties((prevProperties) => [
        ...prevProperties,
        {
          ...propertyData,
          id: savedProperty?.id,
          propertyId: savedProperty?.propertyId,
          lastUpdate: new Date()?.toISOString()
        }]
        );

        showNotification('تم إضافة العقار بنجاح', 'success');
      } else {
        // Update existing property in Supabase
        await propertyService?.update(editingProperty?.id, propertyData, user?.id);

        // Update local state
        setProperties((prevProperties) =>
        prevProperties?.map((prop) =>
        prop?.id === editingProperty?.id ?
        { ...prop, ...propertyData, lastUpdate: new Date()?.toISOString() } :
        prop
        )
        );

        showNotification('تم تحديث العقار بنجاح', 'success');
      }

      setShowPropertyForm(false);
      setEditingProperty(null);
    } catch (error) {
      console.error('Error saving property:', error);
      showNotification(error?.message || 'فشل في حفظ العقار', 'error');
    }
  };

  // Add this block - Define propertyActions before return statement
  const propertyActions = actionBoxesConfig;

  const handleStatusChange = async (propertyId, newStatus) => {
    try {
      await propertyService?.update(propertyId, { status: newStatus }, user?.id);
      
      setProperties((prevProperties) =>
        prevProperties?.map((prop) =>
          prop?.id === propertyId ?
            { ...prop, status: newStatus, updated_at: new Date()?.toISOString() } :
            prop
        )
      );

      setNotification({
        type: 'success',
        title: 'تم التحديث',
        message: 'تم تحديث حالة العقار بنجاح'
      });
    } catch (error) {
      console.error('Error updating property status:', error);
      setNotification({
        type: 'error',
        title: 'خطأ',
        message: error?.message || 'فشل في تحديث حالة العقار'
      });
    }
  };

  const handlePriceUpdate = async (propertyId, newPrice) => {
    try {
      await propertyService?.update(propertyId, { asking_price: newPrice }, user?.id);
      
      setProperties((prevProperties) =>
        prevProperties?.map((prop) =>
          prop?.id === propertyId ?
            { ...prop, asking_price: newPrice, updated_at: new Date()?.toISOString() } :
            prop
        )
      );

      setNotification({
        type: 'success',
        title: 'تم التحديث',
        message: 'تم تحديث السعر بنجاح'
      });
    } catch (error) {
      console.error('Error updating property price:', error);
      setNotification({
        type: 'error',
        title: 'خطأ',
        message: error?.message || 'فشل في تحديث السعر'
      });
    }
  };

  const handleMetricClick = (metricType) => {
    setActiveMetric(metricType);

    // Update filters based on metric clicked
    switch (metricType) {
      case 'total':
        setFilters((prev) => ({ ...prev, status: 'all' }));
        break;
      case 'available':
        setFilters((prev) => ({ ...prev, status: 'متاح' }));
        break;
      case 'rented':
        setFilters((prev) => ({ ...prev, status: 'مؤجر' }));
        break;
      case 'value':
        // Sort by price or show value-related view
        setFilters((prev) => ({ ...prev, status: 'all' }));
        break;
      default:
        break;
    }
  };

  return (
    <Layout userRole={userRole} userName={userName} onQuickMenuToggle={handleQuickMenuToggle}>
      <SubHeader pageTitle="لوحة العقارات" />
      <ActionBoxes
        actions={propertyActions}
        showNewContractModal={showNewContractModal}
        setShowNewContractModal={setShowNewContractModal}
        contractTypeConfig={{}} />

      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="max-w-[1600px] mx-auto space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {calculateMetrics()?.map((metric, index) =>
            <MetricsCard
              key={index}
              icon={metric?.icon}
              title={metric?.title}
              value={metric?.value}
              trend={metric?.trend}
              currency={metric?.currency}
              onClick={metric?.onClick}
              isActive={activeMetric === metric?.type} />

            )}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
            {/* Location Filter */}
            <div className="lg:col-span-3">
              <div className="bg-card rounded-2xl border-2 border-border shadow-warm-md" style={{
                background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)',
                borderColor: 'rgba(184, 134, 11, 0.3)'
              }}>
                <LocationFilter
                  locations={locationData}
                  selectedLocations={selectedLocations}
                  onLocationChange={handleLocationChange}
                  onLocationToggle={handleLocationToggle} />

              </div>
            </div>

            {/* Properties Grid */}
            <div className="lg:col-span-9 space-y-4 md:space-y-6">
              {/* Filter Bar */}
              <div className="bg-card rounded-2xl border-2 border-border shadow-warm-md" style={{
                background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)',
                borderColor: 'rgba(184, 134, 11, 0.3)'
              }}>
                <FilterBar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onSaveFilter={handleSaveFilter}
                  onExport={handleExport}
                  savedFilters={savedFilters} />

              </div>

              {/* Bulk Actions */}
              {selectedProperties?.length > 0 &&
              <div className="bg-card rounded-2xl border-2 border-border shadow-warm-md" style={{
                background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)',
                borderColor: 'rgba(184, 134, 11, 0.3)'
              }}>
                  <BulkActionsToolbar
                  selectedCount={selectedProperties?.length}
                  onBulkAction={handleBulkAction}
                  userRole={userRole} />

                </div>
              }

              {/* Sync Status */}
              <SyncStatusIndicator status={syncStatus} lastSync={new Date()?.toISOString()} />

              {/* Properties Table */}
              {isLoadingProperties ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-4"></div>
                  <p className="text-sm text-muted-foreground">جاري تحميل العقارات...</p>
                </div>
              ) : propertiesError ? (
                <div className="text-center py-12">
                  <div className="text-error mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="text-sm text-error mb-4">{propertiesError}</p>
                  <button
                    onClick={loadProperties}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              ) : filteredProperties?.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    لا توجد عقارات تطابق معايير البحث
                  </p>
                </div>
              ) : (
                <PropertyGrid
                  properties={filteredProperties}
                  selectedProperties={selectedProperties}
                  onPropertySelect={handlePropertySelect}
                  onPropertyClick={handlePropertyClick}
                  userRole={userRole}
                  onPropertyDelete={handleDeleteProperty}
                  onPropertyEdit={handleEditProperty}
                  onStatusChange={handleStatusChange}
                  onPriceUpdate={handlePriceUpdate}
                />
              )}


            </div>
          </div>
        </div>
      </div>
      {/* Property Form Modal */}
      {showPropertyForm &&
      <PropertyFormModal
        isOpen={showPropertyForm}
        onClose={() => {
          setShowPropertyForm(false);
          setEditingProperty(null);
        }}
        onSave={handleSaveProperty}
        property={editingProperty}
        mode={propertyFormMode} />

      }
      {/* Property Details Modal */}
      {selectedProperty &&
      <PropertyDetails
        property={selectedProperty}
        onClose={handleCloseDetails}
        onEdit={handleEditProperty}
        onDelete={handleDeleteProperty}
        userRole={userRole}
        customFields={customFields} />

      }
      {/* Delete Approval Modal */}
      {deleteModalOpen &&
      <DeleteApprovalModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        propertyCount={selectedProperties?.length || 1}
        properties={selectedProperties?.map((id) => properties?.find((p) => p?.id === id))}
        userRole={userRole} />

      }
      {/* Notification Toast */}
      {notification &&
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onClose={() => setNotification(null)} />

      }
      {/* Field Management Modal */}
      {showFieldManagement &&
      <FieldManagementModal
        isOpen={showFieldManagement}
        onClose={() => setShowFieldManagement(false)}
        customFields={customFields}
        onFieldsUpdate={handleFieldsUpdate}
        onSave={handleSaveCustomFields} />

      }
      {/* Properties Popup Modal - Move this block inside Layout */}
      <PropertiesPopupModal
        isOpen={showPropertiesPopup}
        onClose={() => setShowPropertiesPopup(false)}
        properties={filteredProperties}
        userRole={userRole}
      />

    </Layout>);


};

export default PropertyManagementDashboard;